export type PolicySectionTreeSource<SectionId extends string = string> = {
  readonly id: SectionId;
  readonly sectionKey: string;
  readonly heading: string;
  readonly order: number;
  readonly parentSectionId?: SectionId | null;
  readonly nodeKind?: "heading" | "clause";
};

export type PolicySectionTreeNode<SectionId extends string = string> = PolicySectionTreeSource<SectionId> & {
  readonly parentId: SectionId | null;
  readonly children: readonly PolicySectionTreeNode<SectionId>[];
};

const keySegments = (key: string): readonly string[] => {
  const trimmed = key.trim();
  if (!/^[a-z0-9]+(?:[.\-/][a-z0-9]+)+$/i.test(trimmed)) return [trimmed.toLocaleLowerCase()];
  return trimmed.toLocaleLowerCase().split(/[.\-/]+/).filter(Boolean);
};

const canonicalKey = (key: string): string => keySegments(key).join(".");

export const policySectionParentIds = <SectionId extends string>(
  sections: readonly PolicySectionTreeSource<SectionId>[],
): Map<SectionId, SectionId | null> => {
  const sorted = [...sections].sort((a, b) => a.order - b.order);
  const idByKey = new Map<string, SectionId>();
  for (const section of sorted) {
    const key = canonicalKey(section.sectionKey);
    if (key !== "" && !idByKey.has(key)) idByKey.set(key, section.id);
  }

  const parents = new Map<SectionId, SectionId | null>();
  for (const section of sorted) {
    if (section.parentSectionId !== undefined) {
      parents.set(section.id, section.parentSectionId);
      continue;
    }
    const segments = keySegments(section.sectionKey);
    let parentId: SectionId | null = null;
    for (let length = segments.length - 1; length > 0; length -= 1) {
      const candidate = idByKey.get(segments.slice(0, length).join("."));
      if (candidate !== undefined && candidate !== section.id) {
        parentId = candidate;
        break;
      }
    }
    parents.set(section.id, parentId);
  }
  // Invalid legacy relationships must never trap the reader in a cycle.
  for (const section of sorted) {
    const seen = new Set([section.id]);
    let parent = parents.get(section.id) ?? null;
    while (parent !== null) {
      if (seen.has(parent) || !parents.has(parent)) { parents.set(section.id, null); break; }
      seen.add(parent); parent = parents.get(parent) ?? null;
    }
  }
  return parents;
};

export const buildPolicySectionTree = <SectionId extends string>(
  sections: readonly PolicySectionTreeSource<SectionId>[],
): readonly PolicySectionTreeNode<SectionId>[] => {
  const sorted = [...sections].sort((a, b) => a.order - b.order);
  const parents = policySectionParentIds(sorted);
  const childrenByParent = new Map<SectionId | null, PolicySectionTreeSource<SectionId>[]>();
  for (const section of sorted) {
    const parentId = parents.get(section.id) ?? null;
    const children = childrenByParent.get(parentId) ?? [];
    childrenByParent.set(parentId, [...children, section]);
  }

  const build = (section: PolicySectionTreeSource<SectionId>): PolicySectionTreeNode<SectionId> => ({
    ...section,
    parentId: parents.get(section.id) ?? null,
    children: (childrenByParent.get(section.id) ?? []).map(build),
  });
  return (childrenByParent.get(null) ?? []).map(build);
};

export const policyClauseIds = <SectionId extends string>(sections: readonly PolicySectionTreeSource<SectionId>[]): Set<SectionId> => {
  const parents = new Set(policySectionParentIds(sections).values());
  return new Set(sections.filter(section => section.nodeKind !== "heading" && !parents.has(section.id)).map(section => section.id));
};

export const filterPolicySectionTree = <SectionId extends string>(
  nodes: readonly PolicySectionTreeNode<SectionId>[],
  query: string,
): readonly PolicySectionTreeNode<SectionId>[] => {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === "") return nodes;
  const matches: PolicySectionTreeNode<SectionId>[] = [];
  for (const node of nodes) {
    const children = filterPolicySectionTree(node.children, needle);
    if (`${node.sectionKey} ${node.heading}`.toLocaleLowerCase().includes(needle) || children.length > 0) {
      matches.push({ ...node, children });
    }
  }
  return matches;
};

export const policySectionDescendantIds = <SectionId extends string>(
  nodes: readonly PolicySectionTreeNode<SectionId>[],
  sectionId: SectionId,
): Set<SectionId> => {
  const result = new Set<SectionId>();
  const visit = (node: PolicySectionTreeNode<SectionId>): boolean => {
    if (node.id === sectionId) {
      const collect = (item: PolicySectionTreeNode<SectionId>) => {
        result.add(item.id);
        item.children.forEach(collect);
      };
      collect(node);
      return true;
    }
    return node.children.some(visit);
  };
  nodes.some(visit);
  return result;
};
